//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import moment from 'moment-timezone';
import React from 'react';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { getExportCollectionMetrics } from '@apis/content-library/contentLibraryApi';
import {
  ContentLibrarySortType,
  IContentAggregateMediaStats,
  IContentLibraryFiltersOptions,
  IContentLibrarySourcesOptions,
} from '@apis/content-library/contentLibraryApiModel';
import {
  COLLECTION_METRICS_LEARN_MORE_LINK,
  CollectionsTrackingIds,
} from '@collections/constants/CollectionsConstants';
import {
  useGetContentLibrarySettingsQuery,
  useUpdateContentLibrarySettingsMutation,
} from '@collections/queries/collectionsQueries';
import { processStats } from '@collections/utils/processAggregateStats';
import { Alert } from '@components/atoms/alert/Alert';
import { PrimaryAnchor } from '@components/atoms/anchor/Anchor';
import { DownloadButton } from '@components/atoms/download-button/DownloadButton';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import StatBubble from '@components/molecules/stat-bubble/StatBubble';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { useCurrentStateAndParams } from '@hooks/useCurrentStateAndParams';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { MediaLibraryQueryParams } from '@media-library/constants/mediaLibraryConstants';
import { isDefined } from '@utils/filterPredicates';

interface InstagramMetricsProps {
  isLoading: boolean;
  itemStats?: IContentAggregateMediaStats;
  itemCount: number;
  filters?: IContentLibraryFiltersOptions;
  sources?: IContentLibrarySourcesOptions;
  orderBy?: ContentLibrarySortType;
}

export const MediaLibraryMetricsSummary = ({
  filters,
  isLoading,
  itemCount,
  itemStats,
  orderBy,
  sources,
}: InstagramMetricsProps) => {
  const dispatch = useDispatch();
  const { params } = useCurrentStateAndParams<MediaLibraryQueryParams>();

  const { data: settings, isLoading: isSettingsLoading } =
    useGetContentLibrarySettingsQuery();

  const { mutate: updateContentLibrarySettings } =
    useUpdateContentLibrarySettingsMutation();

  const stats = processStats(itemStats);
  const collectionId = params.collectionId;
  const shouldShowCollectionsFilterTooltip =
    settings?.showCollectionsFilterTooltip ?? true;

  const shouldShowOneTimeAlert =
    shouldShowCollectionsFilterTooltip && itemCount > 0;

  const hideOneTimeAlert = () => {
    updateContentLibrarySettings({ showCollectionsFilterTooltip: false });
  };

  const generateExportFilename = (): string | undefined => {
    const date = moment(new Date()).format('MM-DD-YYYY');
    return `${params.collectionId}_metrics_${date}.zip`;
  };

  const handleDownloadCollectionMetrics = async (collectionId: string) => {
    try {
      return await getExportCollectionMetrics({
        collectionId,
        sources,
        filters,
        orderBy,
      });
    } catch (_e) {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Something Went Wrong',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    }
  };

  if (
    !isDefined(itemStats) ||
    itemCount < 1 ||
    isLoading ||
    !isDefined(collectionId)
  ) {
    return null;
  }

  return isSettingsLoading ? (
    <CenteredSpinner />
  ) : (
    <>
      <DownloadContainer>
        <SmallTooltip
          content={
            <div>
              Download metrics for the content in this Collection.{' '}
              {itemCount > 2000 && (
                <span>
                  Per-item metrics are currently limited to the 2,000 most
                  recent items in the Collection.
                </span>
              )}
            </div>
          }
          target={
            <DownloadButton
              aria-label='Download metrics for the content in this Collection.'
              fileName={generateExportFilename()}
              onClick={() => handleDownloadCollectionMetrics(collectionId)}
              trackingId={CollectionsTrackingIds.DOWNLOAD_METRICS_BUTTON}
            />
          }
        />
      </DownloadContainer>
      {shouldShowOneTimeAlert && (
        <Alert
          onDismiss={hideOneTimeAlert}
          trackingId={CollectionsTrackingIds.HIDE_ONE_TIME_ALERT}
          type='warning'
        >
          To report on a subset of posts, scroll down on the right to filter by
          a hashtag or username.{' '}
          <StyledPrimaryAnchor
            href={COLLECTION_METRICS_LEARN_MORE_LINK}
            target='_blank'
            trackingId={CollectionsTrackingIds.LEARN_MORE_ALERT_LINK}
          >
            Learn more.
          </StyledPrimaryAnchor>
        </Alert>
      )}
      <MetricContainer>
        {stats.map((stat) => (
          <StatBubble
            key={stat.label}
            helpText={stat.tooltip}
            title={stat.label}
            value={stat.value}
          />
        ))}
      </MetricContainer>
    </>
  );
};

const DownloadContainer = styled.div`
  display: flex;
  justify-content: end;
`;

const MetricContainer = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: ${theme.spacings[3]};
    width: 100%;
  `,
);

const StyledPrimaryAnchor = styled(PrimaryAnchor)(
  ({ theme }) => `
    &&&{
      color: ${theme.colors.orange6}; 
    }
  `,
);
